import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { OrganizationService, OrganizationInfo, NotifyCustomerToCloseDoorMethod, AuthConfiguration, AuthConfigurationSchema } from '../api/api_pb';
import { useClient } from '../connect_api';
import { S3Service } from '../api/api_pb';
import { AuthMethod, CallInVerificationMode } from '../common/v1/types_pb';
import { create } from '@bufbuild/protobuf';
import { isValidEmail, MAX_NOTIFICATION_EMAILS } from '../utils/validation';

const EditOrganizationInfo: React.FC = () => {
    const navigate = useNavigate();
    const organizationClient = useClient(OrganizationService);
    const s3Client = useClient(S3Service);
    const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [originalLogoUrl, setOriginalLogoUrl] = useState<string>('');
    const [authConfig, setAuthConfig] = useState<AuthConfiguration | null>(null);
    const [emailErrors, setEmailErrors] = useState<Record<number, string>>({});

    const fetchOrganizationInfo = useCallback(async () => {
        try {
            const response = await organizationClient.getMyOrganizationInfo({});
            setOrganizationInfo(response.organizationInfo || null);
            if (response.organizationInfo) {
                setOriginalLogoUrl(response.organizationInfo.logoUrl);
            }
            
            // Fetch auth configuration
            try {
                const authResponse = await organizationClient.getAuthConfiguration({});
                setAuthConfig(authResponse.config || null);
            } catch (error) {
                console.error('Failed to fetch auth configuration:', error);
                // If auth config fetch fails, set default config
                const defaultConfig = create(AuthConfigurationSchema, {
                    enabledMethods: [AuthMethod.MOBILE_PIN],
                    venueAccessCodeValidationType: 0,
                    venueAccessCodeLength: 6,
                    venueAccessCodeCharacterType: 0,
                    venueAccessCodeApiUrl: ''
                });
                setAuthConfig(defaultConfig);
            }
        } catch (error) {
            console.error('Failed to fetch organization info:', error);
            toast.error('Failed to load organization info.');
        } finally {
            setLoading(false);
        }
    }, [organizationClient]);

    useEffect(() => {
        fetchOrganizationInfo();
    }, [fetchOrganizationInfo]);


    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const isAuthMethodEnabled = (method: AuthMethod): boolean => {
        return authConfig?.enabledMethods?.includes(method) || false;
    };

    const toggleAuthMethod = (method: AuthMethod, enabled: boolean) => {
        if (!authConfig) return;
        
        const currentMethods = authConfig.enabledMethods || [];
        let newMethods: AuthMethod[];
        
        if (enabled) {
            // Add the method if not already present
            newMethods = currentMethods.includes(method) 
                ? currentMethods 
                : [...currentMethods, method];
        } else {
            // Remove the method
            newMethods = currentMethods.filter(m => m !== method);
        }
        
        setAuthConfig({
            ...authConfig,
            enabledMethods: newMethods
        });
    };

    const handleUpdate = async () => {
        if (!organizationInfo) return;

        // Validate at least one auth method is enabled
        if (!authConfig || authConfig.enabledMethods.length === 0) {
            toast.error('At least one authentication method must be enabled');
            return;
        }

        // Validate machine offline notification emails
        if (organizationInfo.notifyMachineOfflineEmail) {
            const emails = (organizationInfo.machineOfflineNotificationEmails || []).filter(e => e.trim() !== '');
            if (emails.length === 0) {
                toast.error('Please add at least one email address for machine offline notifications');
                return;
            }
            const invalidEmails = emails.filter(e => !isValidEmail(e.trim()));
            if (invalidEmails.length > 0) {
                toast.error(`Invalid email address: ${invalidEmails[0]}`);
                return;
            }
            // Deduplicate emails (case-insensitive)
            const seen = new Set<string>();
            const dedupedEmails = emails
                .map(e => e.trim())
                .filter(e => {
                    const lower = e.toLowerCase();
                    if (seen.has(lower)) return false;
                    seen.add(lower);
                    return true;
                });
            organizationInfo.machineOfflineNotificationEmails = dedupedEmails;
        }

        try {
            let logoUrl = originalLogoUrl;
            if (imageFile) {
                const extension = imageFile.name.split('.').pop() || '';
                const presignedUrlResponse = await s3Client.getUploadUrl({ extension });
                const presignedUrl = presignedUrlResponse.url;

                const uploadResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    body: imageFile,
                    headers: {
                        'Content-Type': imageFile.type,
                    },
                });

                if (!uploadResponse.ok) {
                    toast.dismiss();
                    toast.error('Failed to upload image, please try again later');
                    throw new Error('Failed to upload image');
                }

                logoUrl = presignedUrl.split('?')[0];
            }

            const updatedOrganizationInfo = {
                ...organizationInfo,
                logoUrl,
            };

            await organizationClient.updateOrganizationInfo({
                organizationInfo: updatedOrganizationInfo,
            });
            
            // Update auth configuration
            if (authConfig) {
                try {
                    await organizationClient.updateAuthConfiguration({
                        config: authConfig,
                    });
                } catch (error) {
                    console.error('Failed to update auth configuration:', error);
                    toast.warning('Organization info updated, but auth configuration update failed.');
                }
            }

            toast.dismiss();
            toast.success('Organization info updated successfully');
            navigate('/');
        } catch (error) {
            toast.dismiss();
            toast.error('Failed to update organization info: ' + (error as Error).message);
            console.error('Error:', error);
        }
    };

    return (
        <div className="p-4">
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div>
                    <h2 className="text-lg font-bold">Edit Organization Info</h2>
                    <div className="mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                            <p>{organizationInfo?.name}</p>
                        </div>
                        <div className="border-b border-gray-300 my-2"></div>
                        <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700">MobileNumber Country Code</label>
                            <input
                                type="text"
                                value={organizationInfo?.mobileNumberCountryCode || ''}
                                onChange={(e) => {
                                    setOrganizationInfo((prev) =>
                                        prev ? { ...prev, mobileNumberCountryCode: e.target.value } : null
                                    );
                                }}
                                className="mt-1 block w-full px-1 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.closeDoorDetection || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, closeDoorDetection: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Enable Close Door Detection</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">*If enabled, the customer cannot return to the homepage in 5 minutes with the door open.</label>
                        </div>


                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.enableDoorNotClosedNotification || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, enableDoorNotClosedNotification: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Enable Door Not Closed Notification</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">
                                If enabled, if the door is not closed in a timely manner, the administrator will be notified
                            </label>
                        </div>


                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.keepReadyOnFaultReport || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, keepReadyOnFaultReport: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Keep Trackable Item Ready</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">* If enabled, trackable items will not be marked as pending inspection after customers report damages.</label>
                        </div>
                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.sendPincodeOnLogin || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, sendPincodeOnLogin: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Send Pin Code On Login</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">
                                If enabled, a pin code will be sent to the customer every time they log in.
                            </label>
                        </div>

                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                               Notify Customer To Close Door
                            </label>
                            <label className="block text-sm font-medium text-gray-700 flex space-x-6">
                                <label className="flex items-center space-x-1 cursor-pointer">
                                    <input type="radio"
                                           name="notifyCustomerToCloseDoor"
                                           checked={organizationInfo?.notifyCustomerToCloseDoorMethod === NotifyCustomerToCloseDoorMethod.NONE || organizationInfo?.notifyCustomerToCloseDoorMethod === NotifyCustomerToCloseDoorMethod.UNSPECIFIED}
                                           onChange={() => {
                                               setOrganizationInfo((prev) =>
                                                   prev ? {
                                                       ...prev,
                                                       notifyCustomerToCloseDoorMethod: NotifyCustomerToCloseDoorMethod.NONE
                                                   } : null
                                               );
                                           }}
                                    />
                                    <span>NONE</span>
                                </label>
                                <label className="flex items-center space-x-1 cursor-pointer">
                                    <input type="radio"
                                           name="notifyCustomerToCloseDoor"
                                           checked={organizationInfo?.notifyCustomerToCloseDoorMethod === NotifyCustomerToCloseDoorMethod.SMS}
                                           onChange={() => {
                                               setOrganizationInfo((prev) =>
                                                   prev ? {
                                                       ...prev,
                                                       notifyCustomerToCloseDoorMethod: NotifyCustomerToCloseDoorMethod.SMS
                                                   } : null
                                               );
                                           }}
                                    />
                                    <span>SMS</span>
                                </label>
                                <label className="flex items-center space-x-1 cursor-pointer">
                                    <input type="radio"
                                           name="notifyCustomerToCloseDoor"
                                           checked={organizationInfo?.notifyCustomerToCloseDoorMethod === NotifyCustomerToCloseDoorMethod.VOICE}
                                           onChange={() => {
                                               setOrganizationInfo((prev) =>
                                                   prev ? {
                                                       ...prev,
                                                       notifyCustomerToCloseDoorMethod: NotifyCustomerToCloseDoorMethod.VOICE
                                                   } : null
                                               );
                                           }}
                                    />
                                    <span>VOICE</span>
                                </label>
                            </label>
                        </div>

                        {/* Door Close Notification Timeout - only shown when method is SMS or VOICE */}
                        {(organizationInfo?.notifyCustomerToCloseDoorMethod === NotifyCustomerToCloseDoorMethod.SMS ||
                          organizationInfo?.notifyCustomerToCloseDoorMethod === NotifyCustomerToCloseDoorMethod.VOICE) && (
                            <div className="mt-3 ml-7">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Door Close Notification Timeout (seconds)
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    value={organizationInfo?.doorCloseNotificationTimeoutSeconds || 45}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, doorCloseNotificationTimeoutSeconds: value } : null
                                        );
                                    }}
                                    className="w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <p className="mt-1 text-sm text-gray-500">
                                    How long to wait (in seconds) before notifying the customer to close the door.
                                </p>
                            </div>
                        )}

                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.skipReturnConditionConfirmation || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, skipReturnConditionConfirmation: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Skip Return Condition Confirmation</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">
                                If enabled, customers will not be asked to confirm item condition during returns (items will be automatically marked as not requiring inspection).
                            </label>
                        </div>

                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.notifyCustomerLeaseExpiredSms || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, notifyCustomerLeaseExpiredSms: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Notify Customer When Lease Expires (SMS)</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">
                                If enabled, customers will receive an SMS notification when their rental period expires (item not returned within the lease duration limit). This only applies to rental items, not for-sale items.
                            </label>
                        </div>

                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.expressRentalMode || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, expressRentalMode: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Express Rental Mode</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">
                                If enabled, customers will be automatically logged out after completing a rental (taking or returning an item). A 3-second countdown is shown before logout. This prevents the next user from accidentally inheriting the previous user's session.
                            </label>
                        </div>

                        <div className="border-b border-gray-300 my-2"></div>

                        {/* Machine Offline Notification Section */}
                        <div className="mt-4">
                            <h3 className="text-md font-semibold text-gray-800 mb-3">Machine Offline Notifications</h3>
                            <p className="text-sm text-gray-600 mb-3">Get notified when vending machines go offline</p>

                            <div className="mt-2">
                                <label className="inline-flex items-center mt-1">
                                    <input
                                        type="checkbox"
                                        checked={organizationInfo?.notifyMachineOfflineEmail || false}
                                        onChange={(e) => {
                                            setOrganizationInfo((prev) =>
                                                prev ? { ...prev, notifyMachineOfflineEmail: e.target.checked } : null
                                            );
                                        }}
                                        className="form-checkbox h-5 w-5 text-blue-600"
                                    />
                                    <span className="ml-2 text-gray-700">Enable Machine Offline Email Notifications</span>
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                    If enabled, email notifications will be sent to the specified addresses when a vending machine has been offline for more than 10 minutes.
                                </label>
                            </div>

                            {organizationInfo?.notifyMachineOfflineEmail && (
                                <div className="mt-3 ml-7">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Notification Email Addresses</label>
                                    <div className="space-y-2">
                                        {(organizationInfo?.machineOfflineNotificationEmails || []).map((email, index) => (
                                            <div key={index}>
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="email"
                                                        value={email}
                                                        onChange={(e) => {
                                                            const newEmails = [...(organizationInfo?.machineOfflineNotificationEmails || [])];
                                                            newEmails[index] = e.target.value;
                                                            setOrganizationInfo((prev) =>
                                                                prev ? { ...prev, machineOfflineNotificationEmails: newEmails } : null
                                                            );
                                                            // Clear error on change
                                                            if (emailErrors[index]) {
                                                                setEmailErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[index];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        onBlur={() => {
                                                            const value = email.trim();
                                                            if (value && !isValidEmail(value)) {
                                                                setEmailErrors((prev) => ({ ...prev, [index]: 'Invalid email format' }));
                                                            } else {
                                                                setEmailErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[index];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        placeholder="email@example.com"
                                                        className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${emailErrors[index] ? 'border-red-500' : 'border-gray-300'}`}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newEmails = (organizationInfo?.machineOfflineNotificationEmails || []).filter((_, i) => i !== index);
                                                            setOrganizationInfo((prev) =>
                                                                prev ? { ...prev, machineOfflineNotificationEmails: newEmails } : null
                                                            );
                                                            // Clear error for removed index and re-index remaining errors
                                                            setEmailErrors((prev) => {
                                                                const next: Record<number, string> = {};
                                                                Object.entries(prev).forEach(([k, v]) => {
                                                                    const ki = parseInt(k);
                                                                    if (ki < index) next[ki] = v;
                                                                    else if (ki > index) next[ki - 1] = v;
                                                                });
                                                                return next;
                                                            });
                                                        }}
                                                        className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                                {emailErrors[index] && (
                                                    <p className="mt-1 text-xs text-red-600">{emailErrors[index]}</p>
                                                )}
                                            </div>
                                        ))}
                                        {(organizationInfo?.machineOfflineNotificationEmails || []).length < MAX_NOTIFICATION_EMAILS && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const currentEmails = organizationInfo?.machineOfflineNotificationEmails || [];
                                                    setOrganizationInfo((prev) =>
                                                        prev ? { ...prev, machineOfflineNotificationEmails: [...currentEmails, ''] } : null
                                                    );
                                                }}
                                                className="mt-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-300 rounded-md"
                                            >
                                                + Add Email Address
                                            </button>
                                        )}
                                        {(organizationInfo?.machineOfflineNotificationEmails || []).length >= MAX_NOTIFICATION_EMAILS && (
                                            <p className="mt-2 text-sm text-gray-500">
                                                Maximum of {MAX_NOTIFICATION_EMAILS} email addresses reached.
                                            </p>
                                        )}
                                    </div>
                                    {(organizationInfo?.machineOfflineNotificationEmails || []).length === 0 && (
                                        <p className="mt-2 text-sm text-amber-600">
                                            No email addresses configured. Add at least one email address to receive notifications.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="border-b border-gray-300 my-2"></div>

                        {/* Authentication Configuration Section */}
                        <div className="mt-4" data-testid="auth-configuration-section">
                            <h3 className="text-md font-semibold text-gray-800 mb-3" data-testid="auth-methods-title">Authentication Methods</h3>
                            <p className="text-sm text-gray-600 mb-3" data-testid="auth-methods-description">Configure available authentication methods for kiosk access</p>
                            
                            {/* Mobile + PIN */}
                            <div className="mt-2">
                                <label className="inline-flex items-center mt-1">
                                    <input
                                        type="checkbox"
                                        checked={isAuthMethodEnabled(AuthMethod.MOBILE_PIN)}
                                        onChange={(e) => {
                                            toggleAuthMethod(AuthMethod.MOBILE_PIN, e.target.checked);
                                        }}
                                        className="form-checkbox h-5 w-5 text-blue-600"
                                        disabled={!authConfig}
                                    />
                                    <span className="ml-2 text-gray-700">Mobile + PIN</span>
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                    Traditional SMS verification with PIN code for secure authentication
                                </label>
                            </div>
                            
                            {/* Venue Access Code */}
                            <div className="mt-2">
                                <label className="inline-flex items-center mt-1">
                                    <input
                                        type="checkbox"
                                        checked={isAuthMethodEnabled(AuthMethod.VENUE_ACCESS_CODE)}
                                        onChange={(e) => {
                                            toggleAuthMethod(AuthMethod.VENUE_ACCESS_CODE, e.target.checked);
                                        }}
                                        className="form-checkbox h-5 w-5 text-blue-600"
                                        disabled={!authConfig}
                                    />
                                    <span className="ml-2 text-gray-700">Venue Access Code</span>
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                    Alternative code-based authentication for venue-specific access
                                </label>
                            </div>
                            
                            {/* Mobile Express */}
                            <div className="mt-2">
                                <label className="inline-flex items-center mt-1">
                                    <input
                                        type="checkbox"
                                        checked={isAuthMethodEnabled(AuthMethod.MOBILE_EXPRESS)}
                                        onChange={(e) => {
                                            toggleAuthMethod(AuthMethod.MOBILE_EXPRESS, e.target.checked);
                                        }}
                                        className="form-checkbox h-5 w-5 text-blue-600"
                                        disabled={!authConfig}
                                    />
                                    <span className="ml-2 text-gray-700">Mobile Express</span>
                                    <span className="ml-2 text-amber-600 text-sm font-medium">⚠️ No Verification</span>
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                    Mobile-only authentication without PIN verification
                                </label>
                                {isAuthMethodEnabled(AuthMethod.MOBILE_EXPRESS) && (
                                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded" data-testid="mobile-express-warning">
                                        <p className="text-xs text-amber-800 flex items-center">
                                            <span className="mr-1">⚠️</span>
                                            <strong>Security Warning:</strong> This method has no verification step and relies entirely on customer honesty. Only use in controlled/trusted environments.
                                        </p>
                                    </div>
                                )}
                            </div>
                            
                            <div className="mt-3 p-3 bg-gray-50 rounded">
                                <p className="text-xs text-gray-600">
                                    <strong>Note:</strong> At least one authentication method must be enabled. Changes will take effect immediately for new authentication attempts.
                                </p>
                            </div>
                        </div>

                        <div className="border-b border-gray-300 my-2"></div>

                        {/* Call-In Verification Section */}
                        <div className="mt-4" data-testid="call-in-verification-section">
                            <h3 className="text-md font-semibold text-gray-800 mb-3">Call-In Verification</h3>
                            <p className="text-sm text-gray-600 mb-3">
                                Allow customers to verify their identity by calling a phone number and entering a PIN, as an alternative to SMS OTP
                            </p>

                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Verification Mode</label>
                                <label className="block text-sm font-medium text-gray-700 flex space-x-6">
                                    <label className="flex items-center space-x-1 cursor-pointer">
                                        <input type="radio"
                                               name="callInVerificationMode"
                                               checked={organizationInfo?.callInVerificationMode !== CallInVerificationMode.FALLBACK && organizationInfo?.callInVerificationMode !== CallInVerificationMode.PRIMARY}
                                               onChange={() => {
                                                   setOrganizationInfo((prev) =>
                                                       prev ? { ...prev, callInVerificationMode: CallInVerificationMode.DISABLED } : null
                                                   );
                                               }}
                                        />
                                        <span>Disabled</span>
                                    </label>
                                    <label className="flex items-center space-x-1 cursor-pointer">
                                        <input type="radio"
                                               name="callInVerificationMode"
                                               checked={organizationInfo?.callInVerificationMode === CallInVerificationMode.FALLBACK}
                                               onChange={() => {
                                                   setOrganizationInfo((prev) =>
                                                       prev ? { ...prev, callInVerificationMode: CallInVerificationMode.FALLBACK } : null
                                                   );
                                               }}
                                        />
                                        <span>Fallback</span>
                                    </label>
                                    <label className="flex items-center space-x-1 cursor-pointer">
                                        <input type="radio"
                                               name="callInVerificationMode"
                                               checked={organizationInfo?.callInVerificationMode === CallInVerificationMode.PRIMARY}
                                               onChange={() => {
                                                   setOrganizationInfo((prev) =>
                                                       prev ? { ...prev, callInVerificationMode: CallInVerificationMode.PRIMARY } : null
                                                   );
                                               }}
                                        />
                                        <span>Primary</span>
                                    </label>
                                </label>
                            </div>

                            {(organizationInfo?.callInVerificationMode === CallInVerificationMode.FALLBACK ||
                              organizationInfo?.callInVerificationMode === CallInVerificationMode.PRIMARY) && (
                                <div className="mt-3 ml-7">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Call-in Phone Number
                                    </label>
                                    {organizationInfo?.callInPhoneNumber ? (
                                        <p className="text-gray-900 font-mono" data-testid="call-in-phone-display">{organizationInfo.callInPhoneNumber}</p>
                                    ) : (
                                        <p className="text-amber-600 text-sm" data-testid="call-in-phone-not-configured">Not configured. Please contact VoT admin to set up a call-in phone number.</p>
                                    )}
                                    <p className="mt-1 text-sm text-gray-500">
                                        This number is configured by VoT admin and cannot be changed here.
                                    </p>
                                </div>
                            )}

                            <div className="mt-3 p-3 bg-gray-50 rounded">
                                <p className="text-xs text-gray-600">
                                    <strong>Disabled:</strong> Call-in verification is not available.{' '}
                                    <strong>Fallback:</strong> Shown only after SMS delivery fails or times out.{' '}
                                    <strong>Primary:</strong> Shown as the default verification method (SMS not attempted).
                                </p>
                            </div>
                        </div>

                        <div className="border-b border-gray-300 my-2"></div>

                        {/* Virtual Products Section */}
                        <div className="mt-4">
                            <h3 className="text-md font-semibold text-gray-800 mb-3">Virtual Products</h3>
                            <p className="text-sm text-gray-600 mb-3">
                                Configure integration with an external API to sell virtual products on kiosks
                            </p>
                            <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    organizationInfo?.virtualProductConfig?.enabled
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {organizationInfo?.virtualProductConfig?.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => navigate('/virtual-products')}
                                    className="px-4 py-2 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 border border-indigo-300 rounded-md transition-colors"
                                >
                                    Configure Virtual Products
                                </button>
                            </div>
                        </div>

                        <div className="border-b border-gray-300 my-2"></div>
                        <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700">Logo</label>
                            <input type="file" accept="image/*" onChange={handleImageChange} />
                            {(imageUrl || originalLogoUrl) && (
                                <img
                                    src={imageUrl || originalLogoUrl}
                                    alt="Preview"
                                    className="mt-2 w-32 h-32 object-contain"
                                />
                            )}
                        </div>
                    </div>
                    <div className="mt-4">
                        <button onClick={() => navigate('/')} className="bg-gray-300 text-black p-2 rounded-md">Cancel</button>
                        <button onClick={handleUpdate} className="bg-blue-600 text-white p-2 rounded-md ml-2">Update</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditOrganizationInfo;
